// @flow
import type { ActivityLogEvent } from '../types'
import React, { PureComponent, type Node } from 'react'
import styled from 'styled-components'
import cx from 'classnames'
import i18n from 'live/services/i18n'
import Auth from 'live/services/authorization'
import theme from 'live/services/theme'
import DateService from 'live/services/date'
import DashboardLink from 'live/components/ui/DashboardLink'
import { MessengerForm } from './activity-log-form'
import _ from 'lodash'
import { Permission } from '../utils'
import { deleteActivitylogById } from '../api'
import globalNotification from 'live/services/global-notification'
import globalConfirmation from 'live/services/global-confirmation'
import TimeRange from 'live/components/ui/time-range'

type Props = {
    event: ActivityLogEvent,
    expanded: boolean,
    onEdit: Function,
    onClick: Function
}
type State = {}

const getAttr = (obj, prop, def = '-') => _.get(obj, prop, def)
const getAuthorName = (author: $PropertyType<ActivityLogEvent, 'author'>): string => {
    if (typeof author === 'string') return author

    if (author && typeof author === 'object') return getAttr(author, 'name', getAttr(author, 'username'))

    return '-'
}

export const ActivityLog = class ActivityLog extends PureComponent<Props, State> {

    edit = (e: Event) => {
        e.stopPropagation()

        if (typeof this.props.onEdit === 'function') this.props.onEdit(e, MessengerForm)
    }

    onDelete = e => {
        e.stopPropagation()

        globalConfirmation.danger('Você realmente deseja deletar o registro de atividade?', null, () => {
            deleteActivitylogById(this.props.event.uid)
                .then(r => {
                    globalNotification.info('Registro de atividade excluído com sucesso.')

                    _.isFunction(this.props.onDelete) && this.props.onDelete(this.props.event)
                })
                .catch(e => {
                    globalNotification.error('Não foi possível excluir o registro de atividade')
                })
        })
    }

    render () {
        const { event, expanded, onEdit, dashboardId } = this.props
        const mCreatedAt = DateService.getMoment(event.timestamp)
        const NOT_INFORMED = '-'
        const canManage = Permission.isAdmin || (Auth.user.id === event.authorId && Permission.canCreateActivityLog)
        const containerCls = cx({
            'live-annotation': true,
            'live-annotation--expanded': expanded
        })
        const dashboardSpan = event.dashboardSpan && `${event.dashboardSpan} shifted to ts ${event.createdAt}`

        return (
            <Container className={containerCls} expanded={expanded} onClick={this.props.onClick}>
                {expanded ? (
                    <Actions expanded={expanded}>
                        {canManage && typeof onEdit === 'function' ? (
                            <li>
                                <a title={i18n('editar')} onClick={this.edit}>
                                    <i className="fa fa-edit" />
                                </a>
                            </li>
                        ) : null}

                        {canManage && typeof onEdit === 'function' ? (
                            <li>
                                <a title={i18n('remover')} onClick={this.onDelete}>
                                    <i className="fa fa-trash-o" />
                                </a>
                            </li>
                        ) : null}
                    </Actions>
                ) : null}

                <div className="live-annotation__title">Registro de atividade</div>
                <div className="live-annotation__subtitle">
                    {i18n('by')}{' '}
                    <span className="live-annotation__author">
                        <small>{getAuthorName(event.author)}</small>
                    </span>
                    {', '}
                    <span
                        className="live-annotation__note-time"
                        title={mCreatedAt.format(DateService.getDateTimeFormat())}
                    >
                        {DateService.fromNowCalendar(mCreatedAt)}
                    </span>
                    {event.dashboard && event.dashboardId && !dashboardId ? (
                        <span>
                            {` ${i18n('em')} `}
                            <DashboardLink
                                name={event.dashboard}
                                id={event.dashboardId}
                                span={dashboardSpan}
                                begin={event.begin}
                                end={event.end}
                            />
                        </span>
                    ) : null}
                </div>
                <Content>
                    <Band expanded={expanded}>
                        <div>{getAttr(event, 'rig.name')}</div>
                        <div>{getAttr(event, 'well.name')}</div>
                    </Band>
                    <Subtitle expanded={expanded}>DADOS BÁSICOS</Subtitle>
                    <Attribute>
                        <strong>Posto CSD: </strong>
                        <span>{getAttr(event, 'csd.name')}</span>
                    </Attribute>
                    <Attribute>
                        <strong>{i18n('Activity')+': '}</strong>
                        <span title={event.comments}>{getAttr(event, 'activity.name')}</span>
                    </Attribute>
                    <TimelineAttribute>
                        <TimeRange color="#37a3ef" begin={event.begin} end={event.end}/>
                    </TimelineAttribute>
                    {expanded ? (
                        <div>
                            <Attribute>
                                <strong>{i18n('Begin date')}: </strong>
                                <span>{event.begin ? DateService.formatDateTime(event.begin) : NOT_INFORMED}</span>
                            </Attribute>
                            <Attribute>
                                <strong>{i18n('End date')}: </strong>
                                <span>{event.end ? DateService.formatDateTime(event.end) : NOT_INFORMED}</span>
                            </Attribute>
                            <Attribute>
                                <strong>{i18n('Intervention')}: </strong>
                                <span>{getAttr(event, 'intervention.name')}</span>
                            </Attribute>

                            <Subtitle expanded={expanded}>CLASSIFICAÇÃO</Subtitle>

                            <Attribute>
                                <strong>{i18n('Activity')+': '}</strong>
                                <span>{getAttr(event, 'activity.name')}</span>
                            </Attribute>
                            {/* <Attribute>
                                <strong>Cliente: </strong>
                                <span>{getAttr(event, 'client.name')}</span>
                            </Attribute> */}
                            <Attribute>
                                <strong>{i18n('Client Role')}: </strong>
                                <span>{getAttr(event, 'clientRole.name')}</span>
                            </Attribute>
                            <Attribute>
                                <strong>{i18n('Detailing 1')}: </strong>
                                <span>{getAttr(event, 'detailing1.name')}</span>
                            </Attribute>
                            <Attribute>
                                <strong>{i18n('Detailing 2')}: </strong>
                                <span>{getAttr(event, 'detailing2.name')}</span>
                            </Attribute>
                            <Attribute>
                                <strong>{i18n('Service Nature')+': '}</strong>
                                <span>{getAttr(event, 'serviceNature.name')}</span>
                            </Attribute>

                            <Subtitle expanded={expanded}>VALOR GERADO E ABRANGÊNCIA</Subtitle>

                            <Attribute>
                                <strong>{i18n('SMS Gain')}: </strong>
                                <span>{getAttr(event, 'smsGain.name')}</span>
                            </Attribute>
                            <Attribute>
                                <strong>{i18n('Performance Gain')}: </strong>
                                <span>{getAttr(event, 'performanceGain.name')}</span>
                            </Attribute>

                            <Subtitle expanded={expanded}>INFORMAÇÕES ADICIONAIS</Subtitle>
                            <Attribute>
                                <strong>Tags: </strong>
                                <span>{event.tags ? event.tags.join(', ') : '-'}</span>
                            </Attribute>
                            <Attribute>
                                <strong>{i18n('Comments')}: </strong>
                                <span>{getAttr(event, 'comments')}</span>
                            </Attribute>
                        </div>
                    ) : null}
                </Content>
            </Container>
        )
    }

}

export default function activtyLogEventRenderer (event: Object) {
    if (event.__type === '__annotations' && event.__src === 'activity-log')
        return {
            priority: 99,
            component: ActivityLog
        }
}

// styled
const Subtitle = ({ expanded, children }: { expanded: boolean, children: Node }) => {
    if (!expanded) return null

    return <SectionTitle>{children}</SectionTitle>
}
const SectionTitle = styled.div`
    font-size: 13px;
    margin-top: 25px;
    margin-bottom: 5px;

    &:first-of-type {
        margin-top: 15px;
    }
`

const TimelineAttribute = styled.div`
    margin-top: 10px;
`

const Attribute = styled.div`
    margin: 5px 0;

    strong {
        font-size: 12px;
        font-family: ${theme['font-semibold']};
    }
    span {
        font-size: 13px;
        color: #606a80;
    }
`

const Band = styled.div`
    display: flex;
    align-items: center;
    border-top: 1px solid var(--gray-800);
    border-bottom: 1px solid var(--gray-800);
    font-size: 14px;
    font-family: ${theme['font-semibold']};
    margin-top: ${({ expanded }) => (expanded ? '15px' : '9px')};
    margin-bottom: ${({ expanded }) => (expanded ? '8px' : '3px')};

    > div {
        flex: 1;
        padding: 2px 0 5px;
        text-align: center;
        &:first-of-type {
            border-right: 1px solid var(--gray-800);
        }
    }
`
const Content = styled.div``

const Actions = styled.ul`
    list-style: none;
    position: absolute;
    right: 10px;
    top: 10px;
    display: ${({ expanded }) => (expanded ? 'flex' : 'none')};
    align-items: center;

    > li {
        margin-left: 10px;
        .fa-trash-o {
            position: relative;
            color: ${theme['brand-danger']};
            top: -1px;
        }
    }
`

const Container = styled.div`
    position: relative;
    overflow-x: hidden;
    text-align: left;
    margin: ${({ expanded }) => (expanded ? '0' : '10px 0')};
`
